import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import useTriggerAnotherWorkflowStore, { getTriggerAnotherWorkflowStore } from './useTriggerAnotherWorkflowStore';
import useProjectStore from '../ProjectCanvas/useProjectStore';
import useCanvasStore from '../pages/Canvas/useCanvasStore';
import { getCurrentTime } from '../../utils/getCurrentTime';
import { injectMetadataFields } from '../../utils/buildComponentSavePayload';
import {
  send_component_save_request,
  send_request,
} from '../../utils/Request';
import { loadDraftProject } from '../../utils/canvasApi';
import { deriveMissingTypesInSavedPlaceholders } from '../../utils/LoopModeUtil';
import PlaceholderSelection from './components/PlaceholderSelection';
import Dropdowns from './components/Dropdowns';
import { IndividualComponentLoader } from '../../utils/ComponentLoadingSkeleton';
import { createLoopLink } from '../../utils/CanvasConstants';
import { LoopModeToggle } from '../TriggerFromAWorkflow/components/LoopModeToggle';
import CustomPaper from '../common/CustomPaper';

const TriggerAnotherWorkflowTemplate = (props) => {
  const { component_id, version, projectId: projectIdProp, project_id } = props;

  const [placeholders, setPlaceholders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [projects, setProjects] = useState(null);
  const [components, setComponents] = useState(null);
  const [targetProjectId, setTargetProjectId] = useState('');
  const [targetComponentId, setTargetComponentId] = useState('');
  const [savedPlaceholders, setSavedPlaceholders] = useState([]);
  const [projectsRefreshing, setProjectsRefreshing] = useState(false);
  const [componentsRefreshing, setComponentsRefreshing] = useState(false);
  const [loopMode, setLoopMode] = useState(false);
  const [focus, setFocus] = useState('');
  const [description, setDescription] = useState(null);
  const [status, setStatus] = useState(null);

  const targetProjectIdRef = useRef(targetProjectId);
  const targetComponentIdRef = useRef(targetComponentId);
  const savedPlaceholdersRef = useRef(savedPlaceholders);
  const loopModeRef = useRef(loopMode);
  const focusRef = useRef(focus);

  useEffect(() => { targetProjectIdRef.current = targetProjectId; }, [targetProjectId]);
  useEffect(() => { targetComponentIdRef.current = targetComponentId; }, [targetComponentId]);
  useEffect(() => { savedPlaceholdersRef.current = savedPlaceholders; }, [savedPlaceholders]);
  useEffect(() => { loopModeRef.current = loopMode; }, [loopMode]);
  useEffect(() => { focusRef.current = focus; }, [focus]);

  const prevTargetProjectIdRef = useRef(targetProjectId);
  const prevTargetComponentIdRef = useRef(targetComponentId);

  const store = useMemo(() => getTriggerAnotherWorkflowStore(component_id), [component_id]);
  const useBpmnJoinBehavior = useProjectStore((s) => s.useBpmnJoinBehavior);

  const renderProjects = (data) => {
    return data.map(project => ({
      label: project['projectName'] ? project['projectName'] : project['projectId'],
      id: project['projectId'],
    }));
  };

  const renderComponents = (data) => {
    if (!data || !data.components) return [];
    const comps = data.components;
    return comps
      .filter(comp => comp && comp['type'] === 'trigger_from_a_workflow')
      .map((comp) => {
        if (comp['componentData']) {
          return {
            label: comp['componentData']['name']
              ? comp['componentData']['name']
              : comp['componentId'],
            id: comp['componentId'],
          };
        } else {
          return {
            label: comp['componentId'],
            id: comp['componentId'],
          };
        }
      });
  };

  const getComponents = async (projId) => {
    if (!projId) return;
    try {
      const res = await loadDraftProject(projId);
      const projectData = res?.data;
      setComponents(renderComponents(projectData ?? { components: null }));
    } catch (e) {
      setComponents([]);
    }
  };

  const refreshProjects = async () => {
    try {
      setProjectsRefreshing(true);
      const response = await send_request(`project-service/project/getProjectsNames`, '', {});
      setProjects(renderProjects(response.data));
      setProjectsRefreshing(false);
      return true;
    } catch (err) {
      console.error('Error refreshing projects:', err);
      setProjectsRefreshing(false);
      return false;
    }
  };

  const refreshComponents = async () => {
    if (targetProjectIdRef.current) {
      try {
        setComponentsRefreshing(true);
        const response = await loadDraftProject(targetProjectIdRef.current);
        const projectData = response?.data;
        setComponents(renderComponents(projectData ?? { components: null }));
        setComponentsRefreshing(false);
        return true;
      } catch (e) {
        setComponents([]);
        setComponentsRefreshing(false);
        return false;
      }
    }
    return false;
  };

  const removeDeletedPlaceholders = (savedPHs) => {
    let pHs = [];
    for (let i = 0; i < savedPHs.length; i++) {
      let currPH = savedPHs[i];
      if (props.availablePlaceholders[0].placeholders.some(aPH => aPH.key === currPH.key)) {
        pHs.push(currPH);
      }
    }
    return pHs;
  };

  const deriveMissingTypes = (savedPHs) => {
    const availablePHs = props.availablePlaceholders?.[0]?.placeholders || [];
    return deriveMissingTypesInSavedPlaceholders(
      savedPHs,
      { placeholders: availablePHs },
      { sourceKey: 'key', targetKey: 'key' }
    );
  };

  const handleSaveComponent = useCallback(() => {
    let cData = store.getState().getTemplate().data;
    const componentName = store.getState().name;
    const componentDescription = store.getState().componentDescription;

    cData.name = componentName;
    cData.targetProjectId = targetProjectIdRef.current;
    cData.targetComponentId = targetComponentIdRef.current;
    cData.loopMode = loopModeRef.current;
    cData.savedPlaceholders = savedPlaceholdersRef.current;

    let lastModified = getCurrentTime();
    let data = injectMetadataFields({
      componentData: {
        data: cData,
        name: componentName,
        lastModified,
      },
      componentId: component_id,
      type: 'trigger_another_workflow',
      description: componentDescription,
    });

    send_component_save_request(
      'component-service/triggerAnotherWorkflow/data',
      data,
      '',
      'POST'
    )
      .then(() => {
        props.onLastModifiedChanged(lastModified);
        props.showSuccess(data);
        useProjectStore.setState({ savedComponent: true });
      })
      .catch(err => {
        throw err;
      });
  }, []);

  // componentDidMount
  useEffect(() => {
    if (component_id == undefined) return;

    if (store.getState().loaded) {
      props.onComponentNameChanged(store.getState().name);
      props.onLastModifiedChanged(store.getState().lastModified);

      if (!projects || projects.length === 0) {
        send_request('project-service/project/getProjectsNames', '', {}).then((response) => {
          if (response?.data) {
            setProjects(renderProjects(response.data));
          }
        });
      }

      setLoading(false);
      props.setChildTemplateLoaded(true);
      return;
    }

    setLoading(true);

    send_request(
      `project-service/project/component/query/${component_id}/at-version/${version}`,
      '',
      {}
    )
      .then(response => {
        if (
          response.data &&
          response.data.components &&
          response.data.components[0].componentData
        ) {
          let cData = response.data.components[0].componentData;
          store.getState().setTriggerAnotherWorkflowTemplate(response.data.components[0]);
          store.setState({ loaded: true });
          props.onComponentNameChanged(store.getState().name);
          props.onComponentDescriptionChanged(response.data.components[0].description);
          props.onLastModifiedChanged(store.getState().lastModified);

          if (cData?.data) {
            const isProjectIdMissing = !cData.data.targetProjectId && cData.data.targetComponentId;
            const resolvedTargetProjectId = isProjectIdMissing
              ? projectIdProp
              : cData.data.targetProjectId;

            const resolvedTargetComponentId = cData.data.targetComponentId;

            setTargetProjectId(resolvedTargetProjectId);
            setTargetComponentId(resolvedTargetComponentId);

            const processedPlaceholders = deriveMissingTypes(
              removeDeletedPlaceholders(cData.data.savedPlaceholders)
            );

            setSavedPlaceholders(processedPlaceholders);
            setLoopMode(cData.data.loopMode || false);

            if (processedPlaceholders.length < cData.data.savedPlaceholders.length) {
              // Trigger save to clean up deleted placeholders
              setTimeout(() => handleSaveComponent(), 0);
            }

            if (resolvedTargetProjectId) {
              getComponents(resolvedTargetProjectId);
            }
          }

        }
        props.setChildTemplateLoaded(true);
      })
      .then(() => {
        refreshProjects()
          .then(() => {
            setLoading(false);
          })
          .catch(err => {
            setLoading(false);
            throw Error(err.message);
          });
      })
      .catch(err => {
        throw Error(err.message);
      });
  }, []);

  // Sync component name into store
  useEffect(() => {
    const componentName = useProjectStore.getState().componentName;
    if (componentName !== undefined) {
      store.getState().setTriggerAnotherWorkflowName(componentName);
    }
    const unsub = useProjectStore.subscribe(
      (s) => s.componentName,
      (name) => {
        store.getState().setTriggerAnotherWorkflowName(name);
      }
    );
    return unsub;
  }, []);

  // React to placeholder selection
  useEffect(() => {
    const unsub = useProjectStore.subscribe(
      (s) => s.selectedPlaceholder,
      (placeholder) => {
        const activeId = useProjectStore.getState().activeComponentId;
        if (activeId !== null && activeId !== props.component_id) return;
        if (!placeholder || !placeholder['selected'] || typeof placeholder['selected'] !== 'object') return;
        const { style, key, isNotPlaceholder } = placeholder['selected'];
        if (!style) return;
        insertPlaceholder(style.default, key, style, focusRef.current, isNotPlaceholder);
      }
    );
    return unsub;
  }, []);

  // componentDidUpdate equivalent for targetComponentId / targetProjectId
  useEffect(() => {
    const prevTargetComponentId = prevTargetComponentIdRef.current;
    const prevTargetProjectId = prevTargetProjectIdRef.current;
    const hasTargetComponentIdChanged = prevTargetComponentId !== targetComponentId;
    const hasTargetProjectIdChanged = prevTargetProjectId !== targetProjectId;

    if (hasTargetComponentIdChanged && loopMode) {
      useCanvasStore.getState().removeLoopEdgeBySourceAndTargetId(component_id, prevTargetComponentId);
      useCanvasStore.getState().addEdge(createLoopLink(component_id, targetComponentId));
    }

    if (hasTargetProjectIdChanged && targetProjectId) {
      getComponents(targetProjectId);
    }

    prevTargetComponentIdRef.current = targetComponentId;
    prevTargetProjectIdRef.current = targetProjectId;
  }, [targetComponentId, targetProjectId, loopMode]);

  const insertPlaceholder = useCallback((label, text, style, currentFocus, isNotPlaceholder) => {
    if (currentFocus === 'editor' || currentFocus === 'subject') {
      setPlaceholders(prev => [...prev, { label, text, style, focus: currentFocus }]);
    } else {
      toInput(label, text, style, currentFocus, isNotPlaceholder);
    }
  }, []);

  const toInput = useCallback((label, text, style, currentFocus, isNotPlaceholder) => {
    // toInput logic preserved from original class (delegated to child components via props)
  }, []);

  const handleLoopModeToggle = (event) => {
    const isToggled = event.target.checked;
    setLoopMode(isToggled);
    if (isToggled) {
      const currentProjectId = project_id;
      setTargetProjectId(currentProjectId);
      setTargetComponentId('');
      getComponents(currentProjectId);
    } else {
      useCanvasStore.getState().removeLoopEdgeBySourceAndTargetId(
        component_id,
        targetComponentId
      );
    }
  };

  const updateState = async (newState) => {
    if ('targetProjectId' in newState) setTargetProjectId(newState.targetProjectId);
    if ('targetComponentId' in newState) setTargetComponentId(newState.targetComponentId);
    if ('savedPlaceholders' in newState) setSavedPlaceholders(newState.savedPlaceholders);
    if ('loopMode' in newState) setLoopMode(newState.loopMode);
    if ('projects' in newState) setProjects(newState.projects);
    if ('components' in newState) setComponents(newState.components);
    if ('projectsRefreshing' in newState) setProjectsRefreshing(newState.projectsRefreshing);
    if ('componentsRefreshing' in newState) setComponentsRefreshing(newState.componentsRefreshing);
  };

  return loading ? (
    <IndividualComponentLoader />
  ) : (
    <CustomPaper>
      {!useBpmnJoinBehavior && (
        <LoopModeToggle loopMode={loopMode} onChange={handleLoopModeToggle} />
      )}
      <Dropdowns
        projects={projects}
        components={components}
        updateState={updateState}
        handleSaveComponent={handleSaveComponent}
        getComponents={getComponents}
        targetProjectId={targetProjectId}
        targetComponentId={targetComponentId}
        refreshProjects={refreshProjects}
        refreshComponents={refreshComponents}
        projectsRefreshing={projectsRefreshing}
        componentsRefreshing={componentsRefreshing}
        loopMode={loopMode}
      />
      {targetProjectId && targetComponentId && (
        <PlaceholderSelection
          {...props}
          savedPlaceholders={savedPlaceholders}
          updateState={updateState}
          isLoopMode={loopMode}
        />
      )}
    </CustomPaper>
  );
};

export default TriggerAnotherWorkflowTemplate;
